import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Button,
  Modal,
  Space,
  Alert,
  Typography,
  Tag,
  Card,
} from 'antd';
import { getTrailerPlan } from '@/services/xiaohongshu';
import FullScreenSpin from '@/components/FullscreenSpin';

interface TrailerProps {
  name: string;
  visible: boolean;
  onCancel: (v: boolean) => void;
  onFinish: (plan: Plan | null) => void; // Renamed for clarity
}

// Define a type for your plan objects for better type safety
interface Plan {
  id: string;
  start_time: number; // Assuming timestamp
  title: string;
  plan_id: string; // e.g., "已发布预告"
  subscribe_num: number;
}

const { Title, Text } = Typography;

const TrailerEditor: React.FC<TrailerProps> = ({
  name,
  visible,
  onCancel,
  onFinish,
}) => {
  const [processing, setProcessing] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selected, setSelected] = useState<string>(''); // Track selected plan ID

  useEffect(() => {
    const fetchTrailerPlans = async () => {
      if (visible) {
        setProcessing(true);
        try {
          const response = (await getTrailerPlan({ name })) as any; // Cast to your Plan interface
          if (response.resp.response && response.resp.response.length > 0) {
            setPlans(response.resp.response.map((r: any) => r.trailer_info));
          }
        } catch (error) {
          console.error('Failed to fetch trailer plans:', error);
          setPlans([]); // Set to empty array on error
        } finally {
          setProcessing(false);
        }
      }
    };

    fetchTrailerPlans();
  }, [visible]);

  const handleAssociatePlan = (plan: Plan) => {
    // Call onFinish with the selected plan's ID to associate it
    onFinish(plan);
    onCancel(false); // Close modal after selection
  };

  const handleCancelAssociation = () => {
    setSelected(''); // Clear selected plan
    onFinish(null);
    onCancel(false); // Close modal
  };

  return (
    <>
      {processing && <FullScreenSpin />}
      <Modal
        title="直播预告"
        open={visible}
        footer={null}
        onCancel={() => onCancel(false)}
        width={800} // Adjusted width, can be 1000 as before
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message={
              <>
                <div>
                  1. 列表仅展示未来生效的直播预告, 已过期直播不支持关联;
                </div>
                <div>
                  2. 一篇笔记仅能关联一个预告, 替换后已关联预告将取消关联。
                </div>
              </>
            }
            type="info"
            showIcon
          />

          {plans.length === 0 && !processing && (
            <Text>暂无可关联的直播预告。</Text>
          )}

          {plans.map((plan) => (
            <Card key={plan.id} bordered style={{ width: '100%' }}>
              <Row gutter={[16, 16]} align="middle">
                <Col flex="auto">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space align="baseline">
                      {plan.start_time && (
                        <Text strong>
                          {new Date(plan.start_time)
                            .toLocaleString('zh-CN', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false,
                            })
                            .replace(/\//g, '-')}
                        </Text>
                      )}
                      <Tag color="blue">已发布预告</Tag>
                    </Space>
                    <Title level={5} style={{ margin: 0 }}>
                      {plan.title || '未命名直播'}
                    </Title>
                    <Text type="secondary">
                      {`${plan.subscribe_num || 0}人已预约`}
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Space direction="vertical" align="end" size="middle">
                    {plan.plan_id === selected ? (
                      <Button danger onClick={() => handleCancelAssociation()}>
                        取消关联
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        onClick={() => handleAssociatePlan(plan)}
                      >
                        关联此预告
                      </Button>
                    )}
                  </Space>
                </Col>
              </Row>
            </Card>
          ))}
        </Space>
      </Modal>
    </>
  );
};

export default TrailerEditor;
